"use client";

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import FormLoginWithEmail from "./FormLoginWithEmail";
import FormLoginWithPhone from "./FormLoginWithPhone";
import { SupportedLanguage } from "@/lib/shared";
import lang from "./lang.json";
import SocialLoginButtons from "@/components/auth/SocialLoginButtons";
import { localizedPath } from "@/lib/localePath";

type LoginTabsProps = {
  initialTab: string;
  locale: SupportedLanguage;
  redirect?: string;
};

export default function LoginTabs({
  initialTab,
  locale,
  redirect,
}: LoginTabsProps) {
  const normalized = initialTab === "phone" ? "phone" : "email";
  const [tab, setTab] = useState<"email" | "phone">(normalized);
  const isFirstTabEffect = useRef(true);

  // After the user switches tabs, sync ?tab= via history (no full reload / no RSC refetch)
  useEffect(() => {
    if (isFirstTabEffect.current) {
      isFirstTabEffect.current = false;
      return;
    }
    const params = new URLSearchParams(window.location.search);
    params.set("tab", tab);
    const search = params.toString();
    const newUrl = `${window.location.pathname}${search ? `?${search}` : ""}`;
    window.history.replaceState(null, "", newUrl);
  }, [tab]);

  return (
    <>
      <Tabs
        dir={locale === "ar" ? "rtl" : "ltr"}
        value={tab}
        onValueChange={(v) => setTab(v as "email" | "phone")}
        className="w-auto md:w-[400px] gap-y-6"
      >
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="email">
            <span>{lang["email"][locale]}</span>
          </TabsTrigger>
          <TabsTrigger value="phone">
            <span>{lang["phone"][locale]}</span>
          </TabsTrigger>
        </TabsList>
        <TabsContent value="email">
          <FormLoginWithEmail locale={locale} />
        </TabsContent>
        <TabsContent value="phone">
          <FormLoginWithPhone locale={locale} />
        </TabsContent>
      </Tabs>

      <div className="flex flex-col gap-y-4">
        <div className="flex items-center">
          <div className="flex-grow border-t border-gray-300"></div>
          <span className="px-4 text-gray-500 text-sm">
            {lang["continue-with"][locale]}
          </span>
          <div className="flex-grow border-t border-gray-300"></div>
        </div>

        <SocialLoginButtons />

        <p className="text-center text-gray-600">
          {lang["no-account"][locale]}
          <Link
            to={localizedPath(
              locale,
              `/auth/register?tab=${tab}&redirect=${encodeURIComponent(redirect ?? "")}`,
            )}
            className="text-primary font-medium hover:underline"
          >
            {lang["create-account"][locale]}
          </Link>
        </p>
      </div>
    </>
  );
}
